import React, { useId, useMemo, useCallback } from "react";
import { Outlet, PathMatch, useLocation, useMatch, useNavigate, useParams, useResolvedPath } from "react-router-dom";
import { FaChevronLeft } from "react-icons/fa";
import CustomMenu from "core/widgets/customMenu.widget";
import { createMenuItems, MenuItems, AdminMenuItems } from "core/layout";
import paths from "paths";
import {
  AuthActionTypes,
  useStateContext,
  useStateDispatcher,
} from "state";
import { Navbar, NavDropdown, NavItem } from "core/components/nav-bar";
import "scss/account-layout.scss";

const HideBackButton = "adhoc_report";

interface NavConfig {
  match: (location: string) => boolean
  getContent: (params: NavParams) => NavContent
  id: string,
}

interface NavParams {
  accountName?: string
  projectId?: string
}

interface NavContent {
  prefix: string
  link: string
}

// configuration for how the logo should appear if none match the
// last one should be used as a default
const logoConfig: Array<NavConfig> = [
  {
    id: paths.authenticated.AUDIENCES,
    match: (location) => location.includes(paths.authenticated.AUDIENCES),
    getContent: ({ projectId }) => ({
      link: paths.authenticated.PROJECTS,
      prefix: `Project ${projectId}`,
    }),
  },
  // default navbar logo for all account based pages
  {
    id: "default",
    match: (_) => true,
    getContent: ({accountName}) => ({
      link: `/${paths.authenticated.USER_ACCOUNTS}`,
      prefix: `${accountName}`
    })
  }
]

const AccountLayout: React.FC = () => {
  const reactId = useId();
  const { id, projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedTab = location.state;

  const excludeBackButton = useMemo(() => {
    return location.pathname.includes(HideBackButton);
  }, [location]);

  const dispatcher = useStateDispatcher();
  const state = useStateContext();

  const logoutCallback = useCallback(() => {
    dispatcher({ type: AuthActionTypes.LOGOUT });
    navigate(`/${paths.open.LOGIN}`);
  }, []);

  const menuItems = useMemo(() => {
    let menu = createMenuItems(MenuItems, navigate, logoutCallback);
    if (state.user?.isAdmin) {
      menu.splice(1, 0, ...createMenuItems(AdminMenuItems, navigate, () => {}));
    }
    return menu;
  }, [state.user, navigate]);

  const role = useMemo(() => {
    return state.user?.roles.find((value) => value.accountNumber === id);
  }, [state.user, id]);

  const displayNavItem = (to: string) => {
    const resolved = useResolvedPath(to);
    return useMatch({ path: resolved.pathname, end: true });
  }

  const config = logoConfig.find((entry) => entry.match(location.pathname)) || logoConfig.pop()!
  const navParams = { accountName: role?.accountName, projectId}
  const content = config.getContent(navParams)

  return (
    <div className="account-layout">
      <Navbar
        logo={
          <>
            {!excludeBackButton && (
              <FaChevronLeft
                onClick={() => navigate(content.link)}
                style={{ cursor: "pointer" }}
              />
            )}{" "}
          </>
        }
        prefixText={content.prefix}
        assistiveToolbar={
          <CustomMenu
            title={state.user?.username}
            subtitle={
              state.user?.roles?.length! > 1
                ? state.user?.organizationName
                : state.user?.roles[0]?.accountName
            }
            menuItems={menuItems}
          />
        }
      >
        {displayNavItem(paths.authenticated.PROJECTS) &&
          <NavItem
            title="Projects"
            to={paths.authenticated.PROJECTS}
          />
        }
        {displayNavItem(paths.authenticated.DASHBOARD) &&
          <NavItem
            title="Dashboard"
            to={paths.authenticated.DASHBOARD}
          />
        }
        {/* state.user && !role?.isAdhoc && (
          <NavItem
            id={`${reactId}-reports`}
            title="Reports"
            onClick={() => navigate(paths.authenticated.REPORTS)}
          />
        ) */}
        </Navbar>
      <Outlet />
    </div>
  );
};

export default AccountLayout;
